import React, { useCallback, useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { FormControlLabel, Grid, makeStyles, Switch, TextFieldSkeleton, Typography } from '..';
import { FieldSelector, LabeledInputOption } from '../FieldSelector/FieldSelector';

interface Props {
  allLabel: string;
  someLabel: string;
  field: string;
  options: LabeledInputOption[];
  onShowAll(): Promise<unknown> | void;
  disabled?: boolean;
  loading: boolean;
  control: Control<FieldValues, object>;
  row?: boolean;
  required?: boolean;
  initialTogle?: boolean;
  hideFieldSelector?: boolean;
  skeletonOverrides?: string;
}

const useStyles = makeStyles((theme) => ({
  switchContainer: {
    display: 'flex',
  },
  hideField: {
    visibility: 'hidden',
  },
  visible: {
    position: 'relative',
    bottom: theme.spacing(1),
  },
}));

const GRACEFUL_FAILURE_UNDO_WAIT_TIME = 1000;

export function AllOrSome({
  allLabel,
  someLabel,
  field,
  options,
  onShowAll,
  loading,
  disabled = false,
  row = false,
  required = false,
  hideFieldSelector = false,
  initialTogle = true,
  skeletonOverrides,
  control,
}: Props) {
  const classes = useStyles();
  const [showingAll, setShowingAll] = useState(initialTogle);

  const handleChange = useCallback(
    async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setShowingAll(!!checked);
      if (checked) {
        try {
          await onShowAll();
        } catch {
          setTimeout(() => setShowingAll(false), GRACEFUL_FAILURE_UNDO_WAIT_TIME);
        }
      }
    },
    [onShowAll],
  );

  return (
    <Grid container className={classes.switchContainer}>
      <Grid item xs={row ? 2 : 12}>
        <FormControlLabel
          label={<Typography variant="body1">{allLabel}</Typography>}
          control={
            <Switch
              color="primary"
              data-testid={`${field}-all-switch`}
              onChange={handleChange}
              value={showingAll}
              checked={showingAll}
              disabled={disabled}
            />
          }
        />
      </Grid>
      <Grid
        item
        xs={row ? 10 : 12}
        data-testid={`${field}-selector`}
        className={showingAll || hideFieldSelector ? classes.hideField : classes.visible}
      >
        {loading ? (
          <TextFieldSkeleton overrides={skeletonOverrides} />
        ) : (
          <FieldSelector required={required} label={someLabel} fieldName={field} options={options} control={control} />
        )}
      </Grid>
    </Grid>
  );
}

export default AllOrSome;
